import * as React from 'react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Tabs, Tab, Paper, Badge } from '@mui/material';
import { styled } from '@mui/material/styles';

/** Redux Packages. */
import { useDispatch, useSelector, useStore } from 'react-redux';

/** Redux Reducers. */
import { setTabValueData, resetTabValueData } from 'shared/store/slices/tabValueSlice';
import { setProgessValueData, resetProgressValueData } from 'shared/store/slices/progressValueSlice';
import { resetFormData } from 'shared/store/slices/filterViewSlice';
import { setSelectValueChange } from 'shared/store/slices/selectionSlice';
import { panelReadReceived } from 'shared/store/slices/shipmentTodoSlice';

/** Dayjs Packages. */
import dayjs from 'dayjs';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import CustomSnackbar, { ReminderSnackbar } from 'shared/ui/CustomSnackbar';
import SocketAlert from 'shared/ui/SocketAlert';
import TabPanel from 'shared/ui/TabPanel';
import { readSocketData } from 'shared/lib/fetchSocket';

import ShipmentControls from './components/ShipmentControls';
import TodoFab from './components/SidePanel/TodoFab';
import ShipmentMainTable from './components/ShipmentMainTable';
import { shipmentReadCache } from './lib/shipmentReadCache';
import {
    oimTableConfig,
    oexTableConfig,
    aimTableConfig,
    aexTableConfig,
    truckingTableConfig,
} from 'data/maintableConfig';

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -17,
        top: 8,
    },
}));

/** Reminder check interval. */
const REMINDER_CHECK_INTERVAL = 30000;
/** Tab configuration. */
const TAB_CONFIGS = [
    { label: 'OCEAN IMPORT', shipment_type: 0, config: oimTableConfig },
    { label: 'OCEAN EXPORT', shipment_type: 1, config: oexTableConfig },
    { label: 'AIR IMPORT', shipment_type: 2, config: aimTableConfig },
    { label: 'AIR EXPORT', shipment_type: 3, config: aexTableConfig },
    { label: 'FTL/LTL', shipment_type: 4, config: truckingTableConfig },
];
export default function Shipments() {
    const dispatch = useDispatch();
    const store = useStore();

    const tabValue = useSelector((state) => state.tabValueForm);
    const socketConnection = useSelector((state) => state.socketStatus.socketStatus);
    const connectionEpoch = useSelector((state) => state.socketStatus.connectionEpoch);
    const todoList = useSelector((state) => state.shipmentTodo.panel);

    /** Which grids/todo list were already read, and on which connection (see shipmentReadCache). */
    const hasReadData = useRef(shipmentReadCache);

    /** Connection on which each grid's rows last reached the screen; the side panel waits for the open tab's grid. */
    const [gridReadyEpochs, setGridReadyEpochs] = useState({});
    const handleGridReady = useCallback(
        (grid, epoch) => setGridReadyEpochs((prev) => (prev[grid] === epoch ? prev : { ...prev, [grid]: epoch })),
        [],
    );
    /** Server component the open tab's grid reads. */
    const openGrid = TAB_CONFIGS[tabValue]?.config.component;

    /** component는 한 번만 생성 - 절대 재생성 안됨 */
    const tabComponents = useMemo(
        () =>
            TAB_CONFIGS.map(({ config }) => (
                <ShipmentMainTable mainConfig={config} hasReadData={hasReadData} onReadSettled={handleGridReady} />
            )),
        [handleGridReady],
    );

    /** badgeCount만 별도 state로 관리 */
    const [badgeCounts, setBadgeCounts] = useState(() => TAB_CONFIGS.map(() => 0));

    const [reminderList, setReminderList] = useState([]);
    /** Reminders currently shown in the top snack stack (may be multiple at once). */
    const [activeReminders, setActiveReminders] = useState([]);

    /** Dismiss one reminder row from the stack. */
    const handleReminderDismiss = (id) => {
        setActiveReminders((prev) => prev.filter((r) => r.id !== id));
    };

    /** Handle to change tabValue. */
    const handleTabChange = (event, newValue) => {
        dispatch(setTabValueData(newValue));
        dispatch(setProgessValueData('inprogress'));
        dispatch(resetFormData());
        dispatch(setSelectValueChange(0));
    };

    /** Check due reminders; append any newly due (same calendar minute) not already shown. */
    const checkDueReminders = useCallback(() => {
        const now = dayjs();
        const newlyDue = reminderList.filter((reminder) => {
            const dueDate = dayjs(reminder.due_date);
            return dueDate.isSame(now, 'minute');
        });

        if (newlyDue.length === 0) return;

        setActiveReminders((prev) => {
            const shownIds = new Set(prev.map((r) => r.id));
            const toAdd = newlyDue.filter((r) => !shownIds.has(r.id));
            if (toAdd.length === 0) return prev;
            return [...prev, ...toAdd];
        });
    }, [reminderList]);

    /** Named website title. */
    useEffect(() => {
        document.title = 'Shipments | Operation System';
        dispatch(resetTabValueData());
        dispatch(resetProgressValueData());
    }, [dispatch]);

    /** todoList 변경 시 badgeCount만 업데이트 */
    useEffect(() => {
        const counts = TAB_CONFIGS.map(
            ({ shipment_type }) =>
                todoList.filter((val) => val.display_panel === true && val.shipment_type === shipment_type).length,
        );
        setBadgeCounts(counts);
    }, [todoList]);

    /** Read reminders from websocket on every (re)connection. */
    useEffect(() => {
        if (socketConnection) {
            readSocketData(dispatch, '', 'row', 'il_alert', null, null, (receivedData) => {
                setReminderList(receivedData);
            });
        }
    }, [dispatch, socketConnection, connectionEpoch]);

    /**
     * Read the side panel cards once the open tab's rows are on screen. The panel receives
     * only the cards to handle today, not every todo of every shipment, and it waits for the
     * grid because the operator opens Shipments for the rows: read together, the panel held
     * the rows back. A late reply of a tab the operator already left does not count, so the
     * panel does not start while the open tab is still loading. The cards are read again
     * after each reconnection so todos teammates created or closed during the outage appear;
     * a failed read is retried on the next connection.
     */
    useEffect(() => {
        if (!socketConnection || gridReadyEpochs[openGrid] !== connectionEpoch) return;
        const todoListStale = hasReadData.current.todoEpoch !== connectionEpoch;
        if (!todoListStale) return;
        hasReadData.current.todoEpoch = connectionEpoch;
        const startedRevision = store.getState().shipmentTodo.revision;
        readSocketData(dispatch, '', 'row', 'il_shipment_todo_panel', null, null, (receivedData) => {
            if (store.getState().socketStatus.connectionEpoch !== connectionEpoch) return;
            dispatch(panelReadReceived({ cards: receivedData.cards, startedRevision }));
        }, () => {
            if (hasReadData.current.todoEpoch === connectionEpoch) hasReadData.current.todoEpoch = null;
        });
    }, [dispatch, store, socketConnection, connectionEpoch, gridReadyEpochs, openGrid]);

    /** Check due reminders every 30 seconds. */
    useEffect(() => {
        if (reminderList.length === 0) return;

        checkDueReminders();
        const interval = setInterval(checkDueReminders, REMINDER_CHECK_INTERVAL);

        return () => clearInterval(interval);
    }, [reminderList, checkDueReminders]);

    return (
        <Box>
            <SocketAlert />
            <Paper sx={customized_style.itemPaper_main}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 0, m: 0, height: '36px' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        textColor="inherit"
                        variant="fullWidth"
                        sx={{
                            ...customized_style.itemTabs,
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#f1f1f1',
                            },
                        }}
                    >
                        {TAB_CONFIGS.map((tab, index) => (
                            <Tab
                                key={index}
                                label={
                                    <StyledBadge badgeContent={badgeCounts[index]} color="primary">
                                        {tab.label}
                                    </StyledBadge>
                                }
                                sx={{
                                    ...customized_style.itemTab,
                                    borderTopLeftRadius: index === 0 ? '20px' : 0,
                                    borderTopRightRadius: index === TAB_CONFIGS.length - 1 ? '20px' : 0,
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>
                {TAB_CONFIGS.map((tab, index) => (
                    <TabPanel key={index} value={tabValue} index={index}>
                        <Box sx={{ p: 1 }}>
                            <ShipmentControls />
                            {tabComponents[index]}
                        </Box>
                    </TabPanel>
                ))}
            </Paper>

            <TodoFab reminderList={reminderList} setReminderList={setReminderList} />
            <CustomSnackbar />
            <ReminderSnackbar reminders={activeReminders} onDismiss={handleReminderDismiss} />
        </Box>
    );
}
